// Plantillas de inspección (F3a): las base del sistema (solo lectura: se ven y se duplican), la biblioteca opcional del
// Anexo 1 (la empresa la activa duplicándola; ejemplos POR VALIDAR) y las propias de la empresa (ilimitadas: secciones
// e ítems con su tipo de respuesta y "no cumple genera hallazgo"). Editar una propia no cambia inspecciones pasadas:
// cada inspección guarda la foto de la plantilla (M41).

import React, { useState } from 'react';
import {
  activarPlantilla, actualizarPlantilla, borrarPlantilla, cargarEstructura, crearPlantilla, DatosPlantilla, duplicarPlantilla,
} from '../../lib/inspecciones/insp-datos';
import { editableDesdeFoto, itemVacio, ItemEditable, PUEDE_FALLAR, SeccionEditable, validarPlantilla } from '../../lib/inspecciones/insp-logica';
import type { CategoriaPlantilla, FilaPlantilla, FotoPlantilla, Frecuencia, ReglaFoto, TipoRespuesta } from '../../lib/inspecciones/insp-tipos';
import {
  ETIQUETA_CATEGORIA, ETIQUETA_FRECUENCIA, ETIQUETA_REGLA_FOTO, ETIQUETA_RIESGO, ETIQUETA_TIPO_RESPUESTA, FRECUENCIAS, RIESGOS,
} from '../../lib/inspecciones/insp-tipos';
import { Campo, ModalConfirmar, NumeroValor } from '../pe/ComponentesPE';
import { generateChecklist, perfilParaMotor } from '../../lib/compliance-engine';
import { codigoLegal } from '../../lib/menu';

// Lote A: ítems del Anexo 1 que una pregunta puede evidenciar, con su número legal y título (se guarda el id interno del
// Checklist: así nadie escribe "GT-21" pensando en el GT 21 legal, que es otro id).
const ORDEN_SECCION = ['gestion_administrativa', 'gestion_tecnica', 'talento_humano', 'procedimientos_operativos', 'servicios_permanentes'];
const OPCIONES_ANEXO1 = generateChecklist(perfilParaMotor(null)).items
  .filter(i => i.numero_legal != null)
  .sort((a, b) => ORDEN_SECCION.indexOf(a.seccion_anexo) - ORDEN_SECCION.indexOf(b.seccion_anexo) || (a.numero_legal ?? 0) - (b.numero_legal ?? 0))
  .map(i => ({ id: i.id, etiqueta: `${codigoLegal(i)} · ${i.titulo}` }));

const msg = (e: unknown) => (e instanceof Error ? e.message : String(e));
const TIPOS: TipoRespuesta[] = ['cumple', 'si_no', 'numero', 'texto', 'foto'];
const REGLAS_FOTO: ReglaFoto[] = ['opcional', 'obligatoria_no_cumple', 'obligatoria'];

export interface PropsPlantillas {
  companyId: string;
  plantillas: FilaPlantilla[];
  puedeEditar: boolean;
  puedeBorrar: boolean;
  usuarioId: string | null;
  onCambio: () => void;
}

type Editor = { id: string | null; datos: DatosPlantilla; secciones: SeccionEditable[] };

function VistaPlantilla({ foto }: { foto: FotoPlantilla }) {
  let n = 0;
  return (
    <div className="pe2-columna" role="region" aria-label={`Contenido de ${foto.name}`}>
      {foto.sections.map(s => (
        <div key={s.id}>
          <h4>{s.title}</h4>
          <ol className="pe2-vista-lista" start={n + 1}>
            {s.items.map(it => { n++; return (
              <li key={it.id}>{it.text} <span className="pe2-nota">· {ETIQUETA_TIPO_RESPUESTA[it.response_type]}{it.unit ? ` (${it.unit})` : ''}
                {it.creates_finding ? ' · genera hallazgo' : ''}{it.reference ? ` · ${it.reference}` : ''}</span></li>
            ); })}
          </ol>
        </div>
      ))}
    </div>
  );
}

export default function PlantillasInsp(p: PropsPlantillas) {
  const [viendo, setViendo] = useState<FotoPlantilla | null>(null);
  const [editor, setEditor] = useState<Editor | null>(null);
  const [errores, setErrores] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');
  const [ocupado, setOcupado] = useState(false);
  const [borrar, setBorrar] = useState<FilaPlantilla | null>(null);

  const sistema = p.plantillas.filter(t => !t.company_id && !t.is_library);
  const biblioteca = p.plantillas.filter(t => !t.company_id && t.is_library);
  const propias = p.plantillas.filter(t => !!t.company_id);

  async function ejecutar(accion: () => Promise<void>) {
    setOcupado(true); setError(''); setOk('');
    try { await accion(); } catch (e) { setError(msg(e)); } finally { setOcupado(false); }
  }

  const ver = (t: FilaPlantilla) => ejecutar(async () => { setEditor(null); setViendo(viendo?.template_id === t.id ? null : await cargarEstructura(t.id)); });
  const duplicar = (t: FilaPlantilla, nombre?: string) => ejecutar(async () => {
    await duplicarPlantilla(t.id, p.companyId, nombre ?? `${t.name} (copia)`);
    p.onCambio(); setOk(t.is_library ? `"${t.name}" quedó activada en tus plantillas.` : `Se creó "${nombre ?? `${t.name} (copia)`}" en tus plantillas. Ya puedes editarla.`);
  });
  const nueva = () => { setViendo(null); setErrores([]); setEditor({ id: null, datos: { name: '', description: null, category: 'propia', default_frequency: null }, secciones: [{ title: '', items: [itemVacio()] }] }); };
  const editar = (t: FilaPlantilla) => ejecutar(async () => {
    setViendo(null); setErrores([]);
    const f = await cargarEstructura(t.id);
    setEditor({ id: t.id, datos: { name: t.name, description: t.description, category: t.category, default_frequency: t.default_frequency }, secciones: editableDesdeFoto(f) });
  });
  const guardar = () => {
    if (!editor) return;
    const e = validarPlantilla(editor.datos.name, editor.secciones);
    setErrores(e);
    if (e.length) return;
    ejecutar(async () => {
      if (editor.id) await actualizarPlantilla(editor.id, editor.datos, editor.secciones, p.usuarioId);
      else await crearPlantilla(p.companyId, editor.datos, editor.secciones, p.usuarioId);
      setEditor(null); p.onCambio(); setOk('Plantilla guardada. Las inspecciones ya hechas conservan la versión con la que se hicieron.');
    });
  };
  const confirmarBorrar = () => ejecutar(async () => {
    if (!borrar) return;
    await borrarPlantilla(borrar.id); setBorrar(null); p.onCambio(); setOk('Plantilla borrada.');
  });

  // Edición de la estructura
  const setSecciones = (f: (s: SeccionEditable[]) => SeccionEditable[]) => setEditor(ed => (ed ? { ...ed, secciones: f(ed.secciones) } : ed));
  const setItem = (si: number, ii: number, cambio: Partial<ItemEditable>) =>
    setSecciones(ss => ss.map((s, k) => (k !== si ? s : { ...s, items: s.items.map((it, j) => (j !== ii ? it : { ...it, ...cambio })) })));
  const mover = <T,>(lista: T[], i: number, d: number) => { const c = [...lista]; const j = i + d; if (j < 0 || j >= c.length) return c; [c[i], c[j]] = [c[j], c[i]]; return c; };

  const fila = (t: FilaPlantilla, acciones: React.ReactNode) => (
    <div key={t.id} className="pe2-tarjeta">
      <b>{t.name}</b>
      <span className="pe2-nota">{ETIQUETA_CATEGORIA[t.category]}{t.default_frequency ? ` · ${ETIQUETA_FRECUENCIA[t.default_frequency]}` : ''}{t.company_id ? ` · versión ${t.version}` : t.is_library ? ` · v${t.version}` : ''}{!t.active ? ' · desactivada' : ''}</span>
      {t.frequency_source && <span className="pe2-nota">Frecuencia: {t.frequency_source}</span>}
      {t.description && <span className="pe2-nota">{t.description}</span>}
      <div className="pe2-evidencia-botones">{acciones}</div>
    </div>
  );

  return (
    <section className="pe2-card" aria-label="Plantillas">
      <div className="pe2-cabecera">
        <h3>Plantillas</h3>
        {p.puedeEditar && <button type="button" className="pe2-btn primario" onClick={nueva} disabled={ocupado}>Nueva plantilla</button>}
      </div>
      {error && <div className="pe2-error" role="alert">{error}</div>}
      {ok && <p className="pe2-ok" role="status">{ok}</p>}
      {viendo && <div className="pe2-card pe2-sub-card"><div className="pe2-cabecera"><h4>{viendo.name}{p.plantillas.some(t => t.id === viendo.template_id && !t.company_id && t.is_library) ? ` · v${viendo.version}` : ''}</h4>
        <button type="button" className="pe2-btn" onClick={() => setViendo(null)}>Cerrar</button></div><VistaPlantilla foto={viendo} /></div>}

      {editor && (
        <div className="pe2-card pe2-sub-card" role="region" aria-label={editor.id ? 'Editar plantilla' : 'Nueva plantilla'}>
          <div className="pe2-grid">
            <Campo etiqueta="Nombre de la plantilla">
              <input type="text" value={editor.datos.name} onChange={e => setEditor({ ...editor, datos: { ...editor.datos, name: e.target.value } })} />
            </Campo>
            <Campo etiqueta="Categoría">
              <select value={editor.datos.category} onChange={e => setEditor({ ...editor, datos: { ...editor.datos, category: e.target.value as CategoriaPlantilla } })}>
                {(Object.keys(ETIQUETA_CATEGORIA) as CategoriaPlantilla[]).map(c => <option key={c} value={c}>{ETIQUETA_CATEGORIA[c]}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Frecuencia sugerida">
              <select value={editor.datos.default_frequency ?? ''} onChange={e => setEditor({ ...editor, datos: { ...editor.datos, default_frequency: (e.target.value || null) as Frecuencia | null } })}>
                <option value="">Sin frecuencia</option>
                {FRECUENCIAS.map(f => <option key={f} value={f}>{ETIQUETA_FRECUENCIA[f]}</option>)}
              </select>
            </Campo>
            <Campo etiqueta="Descripción">
              <input type="text" value={editor.datos.description ?? ''} onChange={e => setEditor({ ...editor, datos: { ...editor.datos, description: e.target.value } })} />
            </Campo>
          </div>
          {editor.secciones.map((s, si) => (
            <div key={si} className="pe2-card" role="group" aria-label={`Sección ${si + 1}`}>
              <div className="pe2-cabecera">
                <Campo etiqueta={`Título de la sección ${si + 1}`}>
                  <input type="text" value={s.title} onChange={e => setSecciones(ss => ss.map((x, k) => (k === si ? { ...x, title: e.target.value } : x)))} />
                </Campo>
                <div className="pe2-evidencia-botones">
                  <button type="button" className="pe2-btn" aria-label={`Subir la sección ${si + 1}`} onClick={() => setSecciones(ss => mover(ss, si, -1))}>↑</button>
                  <button type="button" className="pe2-btn" aria-label={`Bajar la sección ${si + 1}`} onClick={() => setSecciones(ss => mover(ss, si, 1))}>↓</button>
                  <button type="button" className="pe2-btn peligro" onClick={() => setSecciones(ss => ss.filter((_, k) => k !== si))}>Quitar sección</button>
                </div>
              </div>
              {s.items.map((it, ii) => (
                <div key={ii} className="insp-editor-item" role="group" aria-label={`Ítem ${si + 1}.${ii + 1}`}>
                  <div className="ancho">
                    <Campo etiqueta={`Pregunta ${si + 1}.${ii + 1}`}>
                      <input type="text" value={it.text} onChange={e => setItem(si, ii, { text: e.target.value })} />
                    </Campo>
                  </div>
                  <Campo etiqueta="Tipo de respuesta">
                    <select value={it.response_type} onChange={e => {
                      const t = e.target.value as TipoRespuesta;
                      setItem(si, ii, { response_type: t, creates_finding: PUEDE_FALLAR.includes(t) && it.creates_finding, fail_value: t === 'si_no' ? it.fail_value ?? 'no' : null,
                        photo_rule: t === 'foto' ? 'obligatoria' : it.photo_rule });
                    }}>
                      {TIPOS.map(t => <option key={t} value={t}>{ETIQUETA_TIPO_RESPUESTA[t]}</option>)}
                    </select>
                  </Campo>
                  {it.response_type === 'numero' && (
                    <>
                      <Campo etiqueta="Unidad"><input type="text" value={it.unit ?? ''} onChange={e => setItem(si, ii, { unit: e.target.value })} /></Campo>
                      <Campo etiqueta="Mínimo (opcional)" nota="Lo pone la empresa: StorSafe no inventa umbrales.">
                        <NumeroValor valor={it.num_min ?? null} unidad={it.unit || '—'} etiqueta="Mínimo" onChange={v => setItem(si, ii, { num_min: v })} />
                      </Campo>
                      <Campo etiqueta="Máximo (opcional)">
                        <NumeroValor valor={it.num_max ?? null} unidad={it.unit || '—'} etiqueta="Máximo" onChange={v => setItem(si, ii, { num_max: v })} />
                      </Campo>
                    </>
                  )}
                  {PUEDE_FALLAR.includes(it.response_type) && (
                    <label className="pe2-check"><input type="checkbox" checked={it.creates_finding} onChange={e => setItem(si, ii, { creates_finding: e.target.checked })} />
                      "No cumple" genera hallazgo</label>
                  )}
                  {it.response_type === 'si_no' && it.creates_finding && (
                    <Campo etiqueta="Respuesta que genera el hallazgo">
                      <select value={it.fail_value ?? ''} onChange={e => setItem(si, ii, { fail_value: (e.target.value || null) as 'si' | 'no' | null })}>
                        <option value="">Elige</option><option value="no">No</option><option value="si">Sí</option>
                      </select>
                    </Campo>
                  )}
                  {it.creates_finding && (
                    <Campo etiqueta="Riesgo del hallazgo">
                      <select value={it.default_risk} onChange={e => setItem(si, ii, { default_risk: e.target.value as ItemEditable['default_risk'] })}>
                        {RIESGOS.map(r => <option key={r} value={r}>{ETIQUETA_RIESGO[r]}</option>)}
                      </select>
                    </Campo>
                  )}
                  {it.response_type !== 'foto' && (
                    <Campo etiqueta="Foto">
                      <select value={it.photo_rule} onChange={e => setItem(si, ii, { photo_rule: e.target.value as ReglaFoto })}>
                        {REGLAS_FOTO.map(r => <option key={r} value={r}>{ETIQUETA_REGLA_FOTO[r]}</option>)}
                      </select>
                    </Campo>
                  )}
                  <label className="pe2-check"><input type="checkbox" checked={it.required} onChange={e => setItem(si, ii, { required: e.target.checked })} />Obligatoria</label>
                  <Campo etiqueta="Referencia normativa (opcional)">
                    <input type="text" value={it.reference ?? ''} placeholder="Norma y artículo, o &quot;por validar&quot;" onChange={e => setItem(si, ii, { reference: e.target.value })} />
                  </Campo>
                  <Campo etiqueta="Ítem del Anexo 1 (opcional)">
                    <select value={it.anexo_item ?? ''} onChange={e => setItem(si, ii, { anexo_item: e.target.value || null })}>
                      <option value="">Sin vínculo</option>
                      {it.anexo_item && !OPCIONES_ANEXO1.some(o => o.id === it.anexo_item) && <option value={it.anexo_item}>{it.anexo_item}</option>}
                      {OPCIONES_ANEXO1.map(o => <option key={o.id} value={o.id}>{o.etiqueta}</option>)}
                    </select>
                  </Campo>
                  <div className="ancho pe2-evidencia-botones">
                    <button type="button" className="pe2-btn" aria-label={`Subir el ítem ${si + 1}.${ii + 1}`} onClick={() => setSecciones(ss => ss.map((x, k) => (k === si ? { ...x, items: mover(x.items, ii, -1) } : x)))}>↑</button>
                    <button type="button" className="pe2-btn" aria-label={`Bajar el ítem ${si + 1}.${ii + 1}`} onClick={() => setSecciones(ss => ss.map((x, k) => (k === si ? { ...x, items: mover(x.items, ii, 1) } : x)))}>↓</button>
                    <button type="button" className="pe2-btn peligro" onClick={() => setSecciones(ss => ss.map((x, k) => (k === si ? { ...x, items: x.items.filter((_, j) => j !== ii) } : x)))}>Quitar ítem</button>
                  </div>
                </div>
              ))}
              <button type="button" className="pe2-btn" onClick={() => setSecciones(ss => ss.map((x, k) => (k === si ? { ...x, items: [...x.items, itemVacio()] } : x)))}>Agregar ítem</button>
            </div>
          ))}
          <button type="button" className="pe2-btn" onClick={() => setSecciones(ss => [...ss, { title: '', items: [itemVacio()] }])}>Agregar sección</button>
          {errores.length > 0 && <div className="pe2-error" role="alert"><ul className="pe2-vista-lista">{errores.map(e => <li key={e}>{e}</li>)}</ul></div>}
          <div className="pe2-acciones">
            <button type="button" className="pe2-btn" onClick={() => setEditor(null)} disabled={ocupado}>Cancelar</button>
            <button type="button" className="pe2-btn primario" onClick={guardar} disabled={ocupado}>Guardar plantilla</button>
          </div>
        </div>
      )}

      <h4>Plantillas base de StorSafe (solo lectura)</h4>
      <div className="pe2-tarjetas">
        {sistema.map(t => fila(t, <>
          <button type="button" className="pe2-btn" onClick={() => ver(t)} aria-label={`Ver ${t.name}`}>Ver</button>
          {p.puedeEditar && <button type="button" className="pe2-btn" onClick={() => duplicar(t)} disabled={ocupado} aria-label={`Duplicar ${t.name}`}>Duplicar</button>}
        </>))}
      </div>
      <h4>Biblioteca del Anexo 1 (opcional)</h4>
      <p className="pe2-nota">Plantillas de ejemplo con las preguntas del Anexo 1. Estructura por validar. Al activarla, se copia a tus plantillas.</p>
      <div className="pe2-tarjetas">
        {biblioteca.map(t => fila(t, <>
          <button type="button" className="pe2-btn" onClick={() => ver(t)} aria-label={`Ver ${t.name}`}>Ver</button>
          {p.puedeEditar && <button type="button" className="pe2-btn" onClick={() => duplicar(t, t.name)} disabled={ocupado} aria-label={`Activar ${t.name}`}>Activar</button>}
        </>))}
      </div>
      <h4>Plantillas de la empresa</h4>
      {propias.length === 0 ? <p className="pe2-nota">Todavía no hay plantillas propias. Crea una o duplica una base.</p> : (
        <div className="pe2-tarjetas">
          {propias.map(t => fila(t, <>
            <button type="button" className="pe2-btn" onClick={() => ver(t)} aria-label={`Ver ${t.name}`}>Ver</button>
            {p.puedeEditar && <button type="button" className="pe2-btn" onClick={() => editar(t)} disabled={ocupado} aria-label={`Editar ${t.name}`}>Editar</button>}
            {p.puedeEditar && <button type="button" className="pe2-btn" onClick={() => duplicar(t)} disabled={ocupado} aria-label={`Duplicar ${t.name}`}>Duplicar</button>}
            {p.puedeEditar && <button type="button" className="pe2-btn" onClick={() => ejecutar(async () => { await activarPlantilla(t.id, !t.active); p.onCambio(); })} disabled={ocupado}>{t.active ? 'Desactivar' : 'Activar'}</button>}
            {p.puedeBorrar && <button type="button" className="pe2-btn peligro" onClick={() => setBorrar(t)} disabled={ocupado} aria-label={`Borrar ${t.name}`}>Borrar</button>}
          </>))}
        </div>
      )}
      <ModalConfirmar abierto={!!borrar} titulo="Borrar la plantilla" ocupado={ocupado}
        mensaje={`Se borra "${borrar?.name ?? ''}". Las inspecciones hechas con ella conservan su copia. Si tiene programaciones, desactívala en vez de borrarla.`}
        onConfirmar={confirmarBorrar} onCancelar={() => setBorrar(null)} />
    </section>
  );
}
